import { type Address, CapacityLimitReason, getChainAddresses } from '@morpho-org/blue-sdk'
import { InterfaceChainUtils, parseApiAsset, ZERO } from '@morpho-org/dapp-core'
import { isDefined } from '@morpho-org/morpho-ts'
import { useTokenBalance } from '@repo/web3'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { isAddressEqual } from 'viem'
import { useBalance } from 'wagmi'

import type { VaultV2Gates } from '@/helpers/vault'

import { isTestVault } from '@/constants/testVaultList'
import { useWeb3Context } from '@/contexts/Web3Context'
import { getParsedValue } from '@/helpers/formatters'
import { getDepositDisabledReason, type UnifiedVaultWarning } from '@/helpers/warnings'
import useVaultV2TooltipMessage from '@/hooks/operation/vault/useVaultV2TooltipMessage'
import { useChangeAfterFetching } from '@/hooks/useChangeAfterFetching'
import { useSpendableBalance } from '@/hooks/useSpendableBalance'
import { useWrappingGasReserve } from '@/hooks/useWrappingGasReserve'
import { computeWrappableNativeBalance } from '@/hooks/wrappingGasReserve.helpers'
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider'

import type { VaultV2ActionPanelData } from '../VaultV2SimpleActionPanel/vaultV2ActionPanel.mapper'

import {
  computeVaultDepositFunding,
  getVaultDepositBalance,
  isAtUnpricedGasTokenLimit,
  resolveStickyMaxAmount
} from '../../hooks/vaultDeposit.helpers'
import { useVaultV2DisabledLabel } from '../VaultV2SimpleActionPanel/VaultV2OperationPanel/hooks/useVaultV2DisabledLabel'
import { useVaultV2 } from './useVaultV2'
import { useVaultV2Position } from './useVaultV2Position'

type VaultV2ByAddress = Pick<VaultV2ActionPanelData, 'address' | 'asset' | 'listed'> & {
  gates: VaultV2Gates
  // Required, not `Partial`: deposits are disabled solely by the API
  // `deposit_disabled` RED warning, so a caller passing vault data from a query
  // that omits the field would silently re-enable deposits on a disabled vault.
  // Typed as the shared warning shape rather than picked from one query, since
  // only `type` and `level` are read (see `isDepositDisabledByWarning`).
  warnings: UnifiedVaultWarning[]
}

interface UseVaultV2DepositLogicParams {
  vaultData: VaultV2ByAddress | null | undefined
  vaultAddress: Address
  isReviewing: boolean
}

export function useVaultV2DepositLogic({
  vaultData: vaultDataParam,
  vaultAddress,
  isReviewing
}: UseVaultV2DepositLogicParams) {
  const { account } = useWeb3Context()
  const { vaultV2WrapDeposit: isWrapDepositEnabled } = useFeatureFlags()

  const vaultAsset = useMemo(
    () => getParsedValue(vaultDataParam?.asset, parseApiAsset),
    [vaultDataParam?.asset]
  )
  const chainId = vaultDataParam?.asset.chain.id
  const isListedVault = vaultDataParam?.listed

  // Derived from server-rendered data, so it's known without any on-chain read — used
  // below to skip the vault/balance fetches on a disabled deposit.
  const depositDisabledReason = useMemo(
    () => getDepositDisabledReason(vaultDataParam?.warnings),
    [vaultDataParam?.warnings]
  )
  const isDepositDisabled = depositDisabledReason !== undefined

  const isWNativeVault = useMemo(() => {
    if (!isWrapDepositEnabled) return false
    if (!vaultAsset?.address || !chainId) return false
    const { wNative } = getChainAddresses(chainId)
    return !!wNative && isAddressEqual(vaultAsset.address, wNative)
  }, [isWrapDepositEnabled, vaultAsset?.address, chainId])

  const nativeSymbol = useMemo(
    () =>
      chainId === undefined
        ? ''
        : (InterfaceChainUtils.tryGetChain(chainId)?.nativeCurrency.symbol ?? ''),
    [chainId]
  )

  const [rawInputAmount, setInputAmount] = useState(0n)
  const [isMax, setIsMax] = useState(false)
  const [isWrapping, setIsWrapping] = useState(false)

  const {
    vaultData,
    isCorrectVaultV2,
    isLoading: isVaultLoading,
    error: vaultDataError,
    refetchVaultData
  } = useVaultV2(vaultAddress, { enabled: !isDepositDisabled })

  const {
    userVaultShares,
    refetch: refetchPosition,
    isLoading: isLoadingPosition,
    error: userVaultPositionError
  } = useVaultV2Position({
    vaultAddress,
    chainId
  })

  const userVaultAssets = useMemo(() => {
    // If the user is not connected, return 0n for JS simulation
    if (!account.isConnected) return ZERO
    if (!isDefined(userVaultShares)) return undefined
    return vaultData?.toAssets(userVaultShares)
  }, [vaultData, userVaultShares, account.isConnected])

  const {
    data: userBalance,
    isLoading: isLoadingBalance,
    isFetching: isFetchingBalance,
    error: userBalanceError
  } = useTokenBalance({
    chainId,
    userAddress: account.address,
    tokenAddress: vaultAsset?.address,
    enabled: !isDepositDisabled,
    refetchInterval: isReviewing ? false : 10_000
  })

  const memoizedUserBalanceError = useChangeAfterFetching(userBalanceError, isFetchingBalance)

  const {
    data: nativeBalanceData,
    isLoading: isLoadingNativeBalance,
    isFetching: isFetchingNativeBalance,
    error: nativeBalanceError
  } = useBalance({
    address: account.address,
    chainId,
    query: {
      enabled: isWNativeVault && account.isConnected,
      refetchInterval: isReviewing ? false : 10_000
    }
  })
  const nativeBalance = nativeBalanceData?.value ?? ZERO
  const memoizedNativeBalanceError = useChangeAfterFetching(
    nativeBalanceError,
    isFetchingNativeBalance
  )

  const {
    reserve: nativeGasReserve,
    isLoading: isLoadingGasReserve,
    isError: isGasReserveError
  } = useWrappingGasReserve({
    chainId,
    enabled: account.isConnected && isWNativeVault
  })
  // A restoring wallet session already reports `isConnected` with its stored address.
  const isNativeBalancePending =
    account.isConnected && (isLoadingNativeBalance || isLoadingGasReserve)

  // Chains paying gas in an ERC-20 that can itself be the vault asset: MAX must hold a gas
  // reserve back, or spending the full balance on the deposit leaves nothing for gas.
  const {
    spendableBalance,
    isGasToken: isGasTokenVault,
    isLoading: isGasTokenReserveLoading,
    isReserveError: isGasTokenReserveError
  } = useSpendableBalance({
    chainId,
    token: vaultAsset?.address,
    balance: userBalance,
    enabled: account.isConnected && !isDepositDisabled
  })

  // Portion of native balance safe to wrap without starving the wallet of gas.
  const wrappableNativeBalance = useMemo(
    () => computeWrappableNativeBalance({ nativeBalance, nativeGasReserve }),
    [nativeBalance, nativeGasReserve]
  )

  const maxDeposit = useMemo(() => {
    const balance = getVaultDepositBalance({
      isWrapping,
      userBalance: spendableBalance,
      wrappableNativeBalance
    })
    if (!isDefined(balance)) return undefined
    // Fall back to the wallet balance until vaultData resolves so MAX shows immediately; the
    // cap-aware max then refines it (the derived input follows while MAX is active).
    if (!isDefined(vaultData)) return { value: balance, limiter: CapacityLimitReason.balance }
    return vaultData.maxDeposit(balance)
  }, [vaultData, spendableBalance, isWrapping, wrappableNativeBalance])

  const inputAmount = resolveStickyMaxAmount(isMax, maxDeposit, rawInputAmount)

  const { erc20Amount, nativeAmount } = useMemo(
    () => computeVaultDepositFunding({ inputAmount, isWrapping }),
    [inputAmount, isWrapping]
  )

  const onMaxClick = useCallback((value: boolean) => {
    setIsMax(value)
  }, [])

  const onWrappingChange = useCallback((value: boolean) => {
    setIsWrapping(value)
  }, [])

  // inputAmount is derived above while MAX is active; this effect only persists the live max
  // into raw state so the amount survives untoggling MAX (typing untoggles isMax, so it never
  // fights user input).
  useEffect(() => {
    if (isMax && isDefined(maxDeposit)) {
      setInputAmount(maxDeposit.value)
    }
  }, [isMax, maxDeposit])

  const finalPosition = useMemo(() => {
    if (!inputAmount || !isDefined(userVaultAssets)) return undefined
    return userVaultAssets + inputAmount
  }, [userVaultAssets, inputAmount])

  const localErrorMessage = useMemo(() => {
    if (isDefined(vaultDataError)) {
      return `Unable to get vault data`
    }

    // Without a gas price the reserve is unknown, so wrapping is withheld rather than mis-sized.
    if (isWrapping && isGasReserveError && inputAmount > wrappableNativeBalance) {
      return 'Turn off wrapping to continue'
    }

    // Without a gas price the reserve is unknown, so MAX and the full balance are refused
    // instead of mis-sizing them.
    if (
      isAtUnpricedGasTokenLimit({
        isGasToken: isGasTokenVault,
        isReserveError: isGasTokenReserveError,
        userBalance,
        inputAmount,
        isMax
      })
    ) {
      return `Leave some ${vaultAsset?.symbol} for gas`
    }

    if (!isDefined(maxDeposit) || isDefined(memoizedUserBalanceError)) {
      return `Unable to get vault deposit amount`
    }

    if (!isDefined(userVaultAssets) || isDefined(userVaultPositionError)) {
      return `Unable to get user vault assets`
    }

    if (inputAmount <= maxDeposit.value) return

    if (maxDeposit.limiter === CapacityLimitReason.balance) {
      return `Insufficient ${vaultAsset?.symbol} balance`
    }
    if (maxDeposit.limiter === CapacityLimitReason.vaultV2_absoluteCap) {
      return `Vault Absolute Cap reached`
    }
    if (maxDeposit.limiter === CapacityLimitReason.vaultV2_relativeCap) {
      return `Vault Relative Cap reached`
    }

    return `Maximum deposit amount exceeded`
  }, [
    inputAmount,
    maxDeposit,
    vaultAsset?.symbol,
    userVaultAssets,
    userVaultPositionError,
    memoizedUserBalanceError,
    vaultDataError,
    isWrapping,
    isGasReserveError,
    wrappableNativeBalance,
    isGasTokenVault,
    isGasTokenReserveError,
    userBalance,
    isMax
  ])

  const tooltipMessage = useVaultV2TooltipMessage({
    address: vaultAddress,
    error: localErrorMessage,
    gates: vaultDataParam?.gates,
    isDeposit: true,
    isDepositDisabled: isDepositDisabled,
    depositDisabledReason,
    chainId
  })

  const isLoading = useMemo(
    () =>
      account.isConnected &&
      (isLoadingPosition || isLoadingBalance || isVaultLoading || isGasTokenReserveLoading),
    [
      account.isConnected,
      isLoadingPosition,
      isLoadingBalance,
      isVaultLoading,
      isGasTokenReserveLoading
    ]
  )

  // MAX falls back to the wallet balance, so it surfaces as soon as the balance resolves.
  const isMaxLoading = useMemo(
    () => account.isConnected && (isLoadingBalance || isGasTokenReserveLoading),
    [account.isConnected, isLoadingBalance, isGasTokenReserveLoading]
  )

  // The wrapping toggle needs the wrappable native balance, so it also waits on the native
  // balance and gas reserve.
  const isWrappingToggleLoading = isLoading || isLoadingNativeBalance || isLoadingGasReserve

  const disabledLabel = useVaultV2DisabledLabel({
    localErrorMessage,
    isInputEmpty: inputAmount === ZERO,
    isListedVault,
    isDeposit: true,
    isCorrectVaultV2,
    gates: vaultDataParam?.gates,
    isTestVault: isTestVault(chainId, vaultAddress),
    isDepositDisabled
  })

  const resetInput = useCallback(() => {
    setInputAmount(ZERO)
    setIsMax(false)
    setIsWrapping(false)
  }, [])

  return {
    inputAmount,
    setInputAmount,
    isMax,
    setIsMax,
    onMaxClick,
    maxDeposit,
    finalPosition,
    localErrorMessage,
    tooltipMessage,
    isLoading,
    isMaxLoading,
    isWrappingToggleLoading,
    disabledLabel,
    isDepositDisabled,
    userVaultAssets,
    resetInput,
    refetchPosition,
    refetchVaultData,
    isWNativeVault,
    nativeSymbol,
    isWrapping,
    onWrappingChange,
    isLoadingGasReserve,
    isNativeBalancePending,
    /** Native balance available to wrap after reserving gas. */
    wrappableNativeBalance,
    nativeAmount,
    erc20Amount,
    error: {
      userBalanceError: memoizedUserBalanceError,
      nativeBalanceError: memoizedNativeBalanceError,
      vaultDataError,
      userVaultPositionError
    }
  }
}
